/*
 * 聊天页 —— 产品首页的对话栏。
 *
 * 舞台（背景、立绘、数值面板、次级入口）在 App 外壳里常驻，这里只负责名牌、
 * 消息流和输入区，贴在手机列的下半部分。
 *
 * ## 数据流
 *
 * 四个请求，都在 `chat-api.ts` / `chat-stream.ts`：
 *
 *   进入页面  POST /api/v1/app/chat/enter     → 今天消息的最新一页（含开场白）
 *   上拉历史  GET  /api/v1/app/chat/timeline  → 今天更早的一页
 *   发一句话  POST /api/v1/app/chat/stream    → SSE 逐字下发（默认走这条）
 *   发一句话  POST /api/v1/app/chat/messages  → 整句返回（宿主读不了流时的退路）
 *
 * **服务端决定 conversationId**，客户端不传也不猜。她要不要主动开口
 * （跨天、久别重逢、上一轮没答完）同样由服务端判定，这里看到什么就画什么。
 *
 * 四份消息来源，合并时按 id 去重：
 *   server     enter + timeline 返回的今天消息
 *   committed  发消息后服务端确认的增量
 *   local      还没确认的乐观条目（发送中 / 失败 / 失败后那句兜底）
 *   stream     正在逐字长出来的那条回复，id 由服务端预分配
 * 服务端确认后**必须**把对应的 local 条目删掉，否则同一句话会出现两遍。
 *
 * 所有「连不上」的分支都由缈缈自己说，不弹白色报错卡片——CHARACTER.md §8
 * 允许她用自己的语气讲失败，但不许伪装成成功、不许沉默。技术原因打到 console，
 * 界面上不出现。
 *
 * 离线时她**只说一句话**：MiaoNotice 里那句。这里曾经还额外插一条离线问候，
 * 结果是失败提示和「我在的，一切都好」同屏，自相矛盾，已删。
 *
 * 数值一律留空：灵力、羁绊、好感读不到就是横杠，不编。
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type {
  ChatEnterView,
  ChatFormKey,
  ChatMeta,
  CharacterAppearanceView,
  ConversationMessage,
  MessagePart,
  SkillProgressPart,
  TurnActivity,
} from "@dreamverse/contracts";
import {
  useChatMode,
  useRelationship,
  useStagePortrait,
  useTopupThank,
} from "../../App";
import { Butterfly, MiaoNotice } from "../../components/AsyncState";
import { useFloatingToast } from "../../components/FloatingToast";
import { IconPower } from "../../components/Icons";
import { MessagePartsRenderer } from "../../components/MessagePartRenderer";
import { musicTrackOf, useMusicPlayer } from "../../components/MusicPlayer";
import { PowerCostNotice } from "../../components/PowerCostNotice";
import { PreferredNamePrompt } from "../../components/PreferredNamePrompt";
import { Button, Sheet } from "../../components/ui";
import { ApiError, apiRequest } from "../../services/api-client";
import { fetchAiDisclosureConfig } from "../../services/compliance-api";
import {
  deleteMessage,
  enterChat,
  fetchEarlierTodayMessages,
  newClientMessageId,
  rateMessage,
  sendChatMessage,
} from "../../services/chat-api";
import {
  STREAM_STALLED,
  openChatStream,
  streamingSupported,
} from "../../services/chat-stream";
import { fallbackLine } from "../../services/offline-preview";
import {
  decidePaintExecution,
  fetchSkillExecution,
  retryPaintExecution,
} from "../../services/paint-api";
import {
  acknowledgeCostNotice,
  fetchCostNotices,
  fetchRelationship,
  type CostNoticeAcknowledgments,
  type CostNoticeKey,
  type RelationshipView,
} from "../../services/me-api";
import { requestTopupThank } from "../../services/payments-api";
import { useResource } from "../../state/resource";
import { useAppearance, useSession, type AppearanceView } from "../../state/session";
import { useSpeak } from "../../services/use-speak";
import { Composer, type ComposerMessageInput } from "./Composer";
import {
  formatTime,
  MessageBubble,
  TypingIndicator,
  type ChatEntry,
} from "./MessageBubble";
import { SystemEventBar } from "./SystemEventBar";
import { TheaterView } from "./TheaterView";
import {
  ChannelEncounter,
  groupChannelEncounters,
  type ChatTimelineItem,
} from "./ChannelEncounter";
import { toolActivityText as copyForToolActivity } from "./tool-activity-copy";
import "./chat.css";

/**
 * 朗读一条消息的灵力消耗。
 *
 * ⚠ 权威定义是服务端的 `SKILL_POWER_COST["voice.tts"]`，这里是**只读镜像**，
 * 仅用于首次提醒文案。**前端不参与计费判断**——客户端声明任何余额增量
 * 都不可信，扣费金额只能由服务端从 SkillDefinition + PricingPolicy 推导。
 */
const TTS_POWER_COST = 2;

/**
 * 朗读一条消息最多等多久。
 *
 * ## ⚠ 这个值必须**大于服务端那条链路的总耗时**，否则朗读永远不可能成功
 *
 * 它曾经没有设置，于是走 `apiRequest` 的默认值 8000ms。而服务端一次朗读是
 * 三段串行：合成（`models.ts` 实测 30 个可见字符 4675ms，上限 400 字符）
 * → 下载供应商那份带过期时间的 WAV（一句话 428~571KB）→ 转存到我方 OSS。
 * 一条正常长度的回复必然超过 8 秒。
 *
 * 更糟的是它不只是「这次失败」：客户端 abort 会经由
 * `messages.controller.ts` 的 `abortOnDisconnect` 传到服务端，
 * 服务端于是取消合成**并退款**。所以 `Message.ttsAssetId` 永远写不进去，
 * 用户点多少次都是同一个超时——这正是「点了播放没效果」的成因。
 *
 * 60 秒的依据：服务端合成侧的上限是 `timeoutForCapability("audio.tts")`，
 * 目前回落到 `LLM_TIMEOUT_MS.default` 的 30 秒，加上下载与转存留一倍余量。
 * 改动服务端超时的人**必须同步抬高这里**，否则又会回到上面那个死循环。
 */
const TTS_TIMEOUT_MS = 60_000;

/**
 * contracts 没有运行时校验；前端代码与尚未重启的旧 API 短暂错版时，新增字段会是
 * undefined。飘窗不能把传输层的不完整值当成真实关系值展示。
 */
function relationshipDeltaOf(meta: ChatMeta): { bond: number; affinity: number } | null {
  if (!Number.isFinite(meta.bondDelta) || !Number.isFinite(meta.affinityDelta)) return null;
  return { bond: meta.bondDelta, affinity: meta.affinityDelta };
}

type RelationshipSnapshot = Pick<
  RelationshipView,
  "level" | "bond" | "bondToNextLevel" | "affinity"
>;

const relationshipSnapshotOf = (view: RelationshipView): RelationshipSnapshot => ({
  level: view.level,
  bond: view.bond,
  bondToNextLevel: view.bondToNextLevel,
  affinity: view.affinity,
});

/**
 * 旧 API 没有随聊天响应下发增量时，用服务端关系视图的前后值兜底。
 * 羁绊升级会把当前级进度归零，所以跨一级时要把升级前尚需的值算进去。
 */
function relationshipDeltaBetween(
  before: RelationshipSnapshot,
  after: RelationshipView,
): { bond: number; affinity: number } | null {
  let bond: number;
  if (after.level === before.level) {
    bond = after.bond - before.bond;
  } else if (after.level === before.level + 1) {
    bond = before.bondToNextLevel + after.bond;
  } else {
    // 一轮交流不应跨越多级；遇到异常数据时宁可不展示，也不编造数值。
    return null;
  }
  return { bond, affinity: after.affinity - before.affinity };
}

/** 按 id 去重的合并。先来的保留，后来的同 id 覆盖不了它。 */
function mergeById(
  base: readonly ConversationMessage[],
  extra: readonly ConversationMessage[],
): ConversationMessage[] {
  const seen = new Set(base.map((m) => m.id));
  return [...base, ...extra.filter((m) => !seen.has(m.id))];
}

interface MessageAppearance {
  name: string;
  portraitUrl: string | null;
  avatarUrl: string | null;
}

/**
 * 按消息自己的 formKey 解析名字、头像与立绘。
 *
 * 旧 API 短暂错版没有 appearances 时，普通形态仍可用普通本地兜底；
 * weakened 宁可不画图，也不能冒用普通形态的头像和立绘。
 */
function appearanceForForm(
  meta: ChatMeta | null,
  formKey: ChatFormKey,
  fallback: AppearanceView,
): MessageAppearance {
  const server: CharacterAppearanceView | undefined = meta?.appearances?.[formKey];
  if (server) {
    return {
      name: `${server.namePrefix}${server.displayName}`,
      portraitUrl: server.defaultPortraitUrl,
      avatarUrl: server.avatarUrl,
    };
  }

  if (meta === null) {
    return {
      name: fallback.name,
      portraitUrl: fallback.portraitUrl,
      avatarUrl: fallback.avatarUrl,
    };
  }

  if (formKey === "normal") {
    const fallbackIsNormal = fallback.prefix === "";
    return {
      name: fallback.baseName,
      portraitUrl: fallbackIsNormal ? fallback.portraitUrl : null,
      avatarUrl: fallbackIsNormal ? fallback.avatarUrl : null,
    };
  }

  return {
    name: `笨笨${fallback.baseName}`,
    portraitUrl: null,
    avatarUrl: null,
  };
}

function messageFormKey(
  message: ConversationMessage,
  current: ChatFormKey,
): ChatFormKey {
  return message.formKey ?? current;
}

export function ChatPage() {
  const session = useSession();
  /*
   * 合规标识默认开启：接口临时失败、前后端短暂错版时都不能把标识隐掉。
   * 只有后台明确保存 enabled=false 后，客户端才关闭它。
   */
  const aiDisclosure = useResource(
    (signal) => fetchAiDisclosureConfig(signal),
    [],
  );
  const aiDisclosureEnabled =
    aiDisclosure.state.phase === "ready" ? aiDisclosure.state.data.enabled : true;
  const appearance = useAppearance();
  const { mode } = useChatMode();
  const { pendingTopupThankOrderIds, completeTopupThank } = useTopupThank();
  const pendingTopupThankOrderId = pendingTopupThankOrderIds[0] ?? null;
  const topupThankPending = pendingTopupThankOrderId !== null;
  const setStagePortrait = useStagePortrait();
  const { playTrack: playMusicTrack } = useMusicPlayer();
  const [localEntries, setLocalEntries] = useState<ChatEntry[]>([]);
  const [committed, setCommitted] = useState<ConversationMessage[]>([]);
  /** enter 给最新一页；这里累积上拉取回的今天更早消息。 */
  const [earlierMessages, setEarlierMessages] = useState<ConversationMessage[]>([]);
  const [timelineCursor, setTimelineCursor] = useState<string | null>(null);
  const [timelineHasMore, setTimelineHasMore] = useState(false);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState<string | null>(null);
  const timelineLoadingRef = useRef(false);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<ReadonlyMap<string, string>>(
    new Map(),
  );
  const imagePreviewUrlsRef = useRef<ReadonlyMap<string, string>>(imagePreviewUrls);
  const [meta, setMeta] = useState<ChatMeta | null>(null);
  const appearancePreloadsRef = useRef(new Map<string, HTMLImageElement>());
  const [streaming, setStreaming] = useState(false);
  /**
   * 正在逐字长出来的那条回复。
   *
   * `messageId` 是**服务端预分配**的，与它最终落库的主键是同一个。
   * 所以 `message.committed` 到达时不需要「删占位再插真身」，
   * 同一个 id 直接接管，中间不会闪。
   *
   * ⚠ 只存正文，不存整条 `ConversationMessage`：`TheaterView` 的翻页器盯着
   * 消息身份（`contentKey`）决定要不要跳回第一页，身份必须在整个流式过程中
   * 保持不变。
   */
  const [stream, setStream] = useState<{ messageId: string; text: string } | null>(null);
  const [toolActivityText, setToolActivityText] = useState<string | null>(null);
  const toolActivityTimerRef = useRef<number | null>(null);
  const toolActivityKeyRef = useRef<string | null>(null);
  const toolActivityVisibleRef = useRef(false);
  /** 气泡模式的滚动容器，以及会改变它可视高度的底部输入区。 */
  const streamViewportRef = useRef<HTMLDivElement>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  /** 上拉补页前的滚动快照；提交 DOM 后用高度差把用户眼前那一行留在原位。 */
  const historyPrependSnapshotRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(
    null,
  );
  const historyPrependingRef = useRef(false);
  const historyPrependReleaseFrameRef = useRef<number | null>(null);
  const [formSheetOpen, setFormSheetOpen] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [speakNotice, setSpeakNotice] = useState<string | null>(null);
  const [pendingSpeakId, setPendingSpeakId] = useState<string | null>(null);
  const [costNotices, setCostNotices] = useState<CostNoticeAcknowledgments | null>(null);
  const [handledPaintConfirmationId, setHandledPaintConfirmationId] = useState<string | null>(null);
  const [paintDecisionPending, setPaintDecisionPending] = useState(false);
  const [paintDecisionError, setPaintDecisionError] = useState<string | null>(null);
  const [activePaintExecutionId, setActivePaintExecutionId] = useState<string | null>(null);
  const [retryingPaintExecutionId, setRetryingPaintExecutionId] = useState<string | null>(null);

  const clearToolActivity = useCallback(() => {
    if (toolActivityTimerRef.current !== null) {
      window.clearTimeout(toolActivityTimerRef.current);
      toolActivityTimerRef.current = null;
    }
    toolActivityKeyRef.current = null;
    toolActivityVisibleRef.current = false;
    setToolActivityText(null);
  }, []);

  const showToolActivity = useCallback((requestId: string, activity: TurnActivity) => {
    const key = `${requestId}:${activity.round}:${activity.phase}:${activity.skillKeys.join(",")}`;
    const nextText = copyForToolActivity(activity, requestId);
    toolActivityKeyRef.current = key;

    if (toolActivityTimerRef.current !== null) {
      window.clearTimeout(toolActivityTimerRef.current);
      toolActivityTimerRef.current = null;
    }

    const reveal = () => {
      if (toolActivityKeyRef.current !== key) return;
      toolActivityTimerRef.current = null;
      toolActivityVisibleRef.current = true;
      setToolActivityText(nextText);
    };

    // 短工具不值得让状态一闪而过；状态已经可见后，下一阶段则即时替换。
    const delayMs = toolActivityVisibleRef.current
      ? 0
      : activity.phase === "tool_running"
        ? 350
        : 220;
    if (delayMs === 0) reveal();
    else toolActivityTimerRef.current = window.setTimeout(reveal, delayMs);
  }, []);

  useEffect(
    () => () => {
      if (toolActivityTimerRef.current !== null) {
        window.clearTimeout(toolActivityTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    imagePreviewUrlsRef.current = imagePreviewUrls;
  }, [imagePreviewUrls]);

  useEffect(
    () => () => {
      for (const url of imagePreviewUrlsRef.current.values()) URL.revokeObjectURL(url);
    },
    [],
  );

  const consumeImagePreview = useCallback((assetId: string, src: string): void => {
    setImagePreviewUrls((current) => {
      if (current.get(assetId) !== src) return current;
      const next = new Map(current);
      next.delete(assetId);
      URL.revokeObjectURL(src);
      return next;
    });
  }, []);

  /**
   * 一次性提醒跟账号走，不跟浏览器走。后台看到的也是这两个确认状态。
   * 读取失败时保留 null：第一次点仍会提醒，不会因一次网络失败而静默跳过告知。
   */
  useEffect(() => {
    if (session.status !== "authenticated" && session.status !== "needs_profile") return;
    const controller = new AbortController();
    setCostNotices(null);
    void fetchCostNotices(controller.signal)
      .then(setCostNotices)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        console.warn("读取灵力提醒状态失败", cause);
        // 读取失败时按“尚未确认”处理：宁可再提醒一次，也不能静默略过扣费告知。
        setCostNotices({ chatImagePower: false, messageVoicePower: false });
      });
    return () => controller.abort();
  }, [session.status]);

  const rememberCostNotice = useCallback((notice: CostNoticeKey) => {
    setCostNotices((current) => ({
      chatImagePower:
        notice === "chat_image_power" ? true : (current?.chatImagePower ?? false),
      messageVoicePower:
        notice === "message_voice_power" ? true : (current?.messageVoicePower ?? false),
    }));
    // 先让当前点击继续；写失败时下次进来会再次提醒，不会把功能本身卡住。
    void acknowledgeCostNotice(notice)
      .then(setCostNotices)
      .catch((cause: unknown) => console.warn("保存灵力提醒状态失败", cause));
  }, []);

  /* -------------------------------------------------------------------- */
  /* 单条消息的操作条                                                      */
  /* -------------------------------------------------------------------- */

  /**
   * 哪一条气泡展开着操作条。**同一时刻只有一条。**
   *
   * 各气泡自己管的话，点第二条时第一条的药丸还开着，屏幕上会同时浮着
   * 好几排按钮，而它们看起来都属于「最近那条消息」。
   */
  const [openActionsId, setOpenActionsId] = useState<string | null>(null);

  /**
   * 本次会话内改过的评价。**只存改动，不存全量。**
   *
   * 服务端的 `MessageFeedback` 目前不随消息一起下发（`ConversationMessage`
   * 里没有这个字段），所以刷新页面后按钮回到未评状态——已经写进库的那一行
   * 还在，运营侧读得到，只是界面上看不出来。
   *
   * 补全的正确做法是在 `ChatAppQuery.messages` 里带上当前用户的 rating，
   * 而不是在前端另发一个批量查询。留着这条注释是为了让下一个人知道
   * 「按钮不记得我点过」不是 bug 而是没做完。
   */
  const [ratings, setRatings] = useState<ReadonlyMap<string, 1 | -1 | null>>(new Map());

  /**
   * 本地已删除的消息 id。
   *
   * 服务端软删之后，`visible` 那份数据要等下一次 `enter` 才会更新。
   * 不在本地滤一遍的话，用户点了删除，那句话还在屏幕上——他会再点一次。
   */
  const [deletedIds, setDeletedIds] = useState<ReadonlySet<string>>(new Set());

  /** 数值变化和普通操作回执都交给应用级通用飘窗。 */
  const { showToast: showFlash, showValueChanges } = useFloatingToast();

  /* -------------------------------------------------------------------- */
  /* 「怎么称呼你」                                                        */
  /* -------------------------------------------------------------------- */

  /**
   * 称呼输入框。**不可跳过。**
   *
   * 要不要出由服务端的 `ChatEnterView.mustAskPreferredName` 决定，判据是
   * **昵称与称呼都是空的**——也就是这个人还没有任何名字。那种状态下她
   * 根本没法称呼对方，实测会当场编（「你是[对方名字]」，模型把提示词模板的
   * 占位符原样吐了出来）。
   *
   * 所以这里没有 `nameSkipped` 之类的跳过状态：框一出现就顶掉输入口，
   * 填完才能说话。填完之后昵称与称呼同时有值，服务端判据永远为假，
   * 一个用户一辈子只会遇到一次。
   *
   * ⚠ **别加回「以后再说」。** 判据是状态不是时机：跳过之后下次进来还会问，
   * 那个「以后」永远不会到来，只会变成每次进来都被拦一下。要么现在填，
   * 要么这个框就该不存在。
   *
   * `submitName` 定义在 `send` 之后：填完名字要接着替用户说一句
   * 「叫我 xx 就好」，那需要 `send`。
   */
  const [nameSubmitting, setNameSubmitting] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  /* 提交成功后本地立刻收起，不等服务端那一轮重取。 */
  const [nameDone, setNameDone] = useState(false);

  /* 关系数据只用来刷 HUD，称呼那一套已经不从这里读了。 */
  const relationship = useRelationship();
  const reloadRelationship = relationship.reload;
  /**
   * 按用户消息保存交流前的服务端关系值。
   * 重试复用同一个 clientMessageId，所以只记首发时的快照，不能被重试覆盖。
   */
  const relationshipBeforeTurn = useRef(new Map<string, RelationshipSnapshot>());

  /* -------------------------------------------------------------------- */
  /* 会话                                                                  */
  /* -------------------------------------------------------------------- */

  /*
   * 进入对话。
   *
   * 跟着 `session.status` 重取：登录成功后必须刷新，否则用户登进来看到的还是
   * 登录前那次 401 的失败态。
   *
   * 这是个 POST（可能新建会话、可能生成开场白），但语义上仍是「取这一页要
   * 显示的东西」，所以照旧走 useResource——重试按钮、取消、失败分类都在那里。
   * 反复进入不会反复打招呼：服务端有静默窗口兜着。
   */
  const chat = useResource<ChatEnterView>(
    (signal) => enterChat(signal),
    [session.status],
  );
  const reloadChat = chat.reload;
  const refreshChat = chat.refresh;

  const ready = chat.state.phase === "ready" ? chat.state.data : null;
  const conversationId = ready?.conversationId ?? null;
  const currentPageMessages = ready?.messages ?? [];
  const serverMessages = mergeById(earlierMessages, currentPageMessages);

  /* 这个人还没有任何名字，必须当场要一个。填完之前发不出消息。 */
  const askName = !nameDone && ready?.mustAskPreferredName === true;

  /*
   * 到账后由独立接口让角色生成感谢。
   *
   * pending 状态先于请求完成进入聊天页，所以用户会立刻看到“正在说话”；
   * 接口返回的是已经落库的角色消息，不在前端拼一句官方感谢文案。
   */
  useEffect(() => {
    if (!pendingTopupThankOrderId || !ready || streaming) return;
    let cancelled = false;
    setReplyOpen(false);

    void requestTopupThank(pendingTopupThankOrderId)
      .then((result) => {
        if (cancelled) return;
        const additions = [result.event, result.message].filter(
          (message): message is ConversationMessage => message !== null,
        );
        if (additions.length > 0) {
          setCommitted((current) => mergeById(current, additions));
        }
        completeTopupThank(pendingTopupThankOrderId);
        // 保留当前画面后台重取：同步新消息、余额与充值后恢复的角色形态，不闪加载页。
        refreshChat();
        reloadRelationship();
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        console.warn("生成充值后的角色致谢失败，已交给后台任务兜底", cause);
        completeTopupThank(pendingTopupThankOrderId);
        // 事件在 LLM 之前落库；即使致谢暂时失败，也要把到账事实拉回时间线。
        refreshChat();
        reloadRelationship();
      });

    return () => {
      cancelled = true;
    };
  }, [
    completeTopupThank,
    pendingTopupThankOrderId,
    ready,
    refreshChat,
    reloadRelationship,
    streaming,
  ]);

  /*
   * 每次整段重取都要把增量清掉，否则那些消息会连同重取回来的历史一起显示两遍。
   * 同理清掉已确认的乐观条目——它们此刻要么已经在历史里，要么属于另一个会话。
   */
  useEffect(() => {
    if (!ready) return;
    setMeta(ready.meta);
    const activeFormKey = ready.meta.activeFormKey ?? ready.meta.formKey;
    const currentAppearance = ready.meta.appearances?.[activeFormKey];
    if (currentAppearance) session.syncAppearance(currentAppearance);
    setEarlierMessages([]);
    // `=== true` / `?? null` 让新前端在 API 尚未重启的短暂错版期安全退化成不分页。
    setTimelineHasMore(ready.hasMore === true);
    setTimelineCursor(ready.nextCursor ?? null);
    setTimelineError(null);
    setCommitted([]);
    setLocalEntries((entries) => entries.filter((e) => e.delivery === "failed"));
    // enter 可能刚在 newDay 开场分支发放了每日灵力，也可能写入了开场羁绊；
    // 重新取关系视图，让 HUD 与全局灵力飘窗立即拿到最新流水。
    reloadRelationship();
  }, [ready, reloadRelationship, session.syncAppearance]);

  /**
   * 向上补今天更早的一页。
   *
   * cursor 由服务端签发，查询会在今天 00:00 停住。这里不传 conversationId：
   * 同一天的多段 conversation 本来就应该连成一条可滚动时间线。
   */
  const loadEarlierMessages = useCallback(async () => {
    if (!timelineHasMore || !timelineCursor || timelineLoadingRef.current) return;
    timelineLoadingRef.current = true;
    setTimelineLoading(true);
    setTimelineError(null);

    try {
      const page = await fetchEarlierTodayMessages(timelineCursor);
      const viewport = streamViewportRef.current;
      if (viewport) {
        historyPrependSnapshotRef.current = {
          scrollHeight: viewport.scrollHeight,
          scrollTop: viewport.scrollTop,
        };
        historyPrependingRef.current = true;
      }
      setEarlierMessages((current) => mergeById(page.messages, current));
      setTimelineHasMore(page.hasMore);
      setTimelineCursor(page.nextCursor);
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : "今天更早的消息暂时没能取回来";
      setTimelineError(message);
      historyPrependingRef.current = false;
      historyPrependSnapshotRef.current = null;
    } finally {
      timelineLoadingRef.current = false;
      setTimelineLoading(false);
    }
  }, [timelineCursor, timelineHasMore]);

  const handleTimelineScroll = useCallback(() => {
    const viewport = streamViewportRef.current;
    if (mode !== "bubble" || !viewport || viewport.scrollTop > 40) return;
    void loadEarlierMessages();
  }, [loadEarlierMessages, mode]);

  /**
   * 朗读。
   *
   * 按钮只在宿主支持音频播放时出现——微信内置浏览器与小程序壳的能力不同，
   * 由 `host-bridge` 探测后随 session 下发，前端不自己猜。
   *
   * 服务端是 `POST /api/v1/app/messages/:id/speech`：一条消息一生只合成一次、
   * 扣一次灵力，之后重播免费。
   */
  const speak = useSpeak({
    supported: session.host.capabilities.audioPlayback,
    onError: (m) => {
      setSpeakNotice(m);
      window.setTimeout(() => setSpeakNotice(null), 3200);
    },
    fetchAudio: useCallback(
      async (messageId: string) =>
        apiRequest<{ url: string; durationMs?: number }>(
          `/api/v1/app/messages/${encodeURIComponent(messageId)}/speech`,
          { method: "POST", timeoutMs: TTS_TIMEOUT_MS },
        ),
      [],
    ),
  });

  const requestSpeak = useCallback(
    (messageId: string) => {
      if (costNotices?.messageVoicePower !== true) {
        setPendingSpeakId(messageId);
        return;
      }
      speak.toggle(messageId);
    },
    [costNotices?.messageVoicePower, speak],
  );

  const appendOfflineLine = useCallback(() => {
    setLocalEntries((entries) => {
      const last = entries[entries.length - 1];
      // 连着失败时不要刷屏，一次未送达配一句就够了。
      if (last?.message.isFallback) return entries;
      return [
        ...entries,
        {
          message: {
            schemaVersion: 1,
            id: `offline-${Date.now()}`,
            conversationId: "",
            sequence: 0,
            role: "assistant",
            parts: [{ type: "text", text: fallbackLine("send") }],
            createdAt: new Date().toISOString(),
            isFallback: true,
          },
        },
      ];
    });
  }, []);

  /*
   * 在飞的那一次发送。
   *
   * **没有「停止」按钮调它了**（2026-08-01 删除）。现在只剩组件卸载时的清理，
   * 以及流式路径内部的空闲看门狗。
   *
   * 那颗按钮删掉的理由：它断得开客户端，却断不掉服务端那一轮——
   * 助手消息照常落库（`chat.orchestrator.ts` 的 appendMessage 无条件执行），
   * 灵力在调模型之前就扣了、不退。用户以为「这一轮不算了」，
   * 下次进来看到的是自己那句话好端端在那儿、还带着她的完整回答。
   *
   * 流式与整句两条路径都往这里放自己的取消函数，所以类型是裸函数
   * 而不是 AbortController。
   */
  const inflight = useRef<(() => void) | null>(null);
  useEffect(() => () => inflight.current?.(), []);

  /**
   * 服务端确认了本轮。**流式与整句两条路径共用这一段。**
   *
   * 抽出来是因为它有三件容易漏的事，漏在哪条路径上都是同一种 bug：
   * 清乐观条目、跨会话时整段重取、刷新 HUD。
   */
  const commit = useCallback(
    (
      turn: {
        conversationId: string;
        messages: readonly ConversationMessage[];
        meta: ChatMeta;
      },
      clientMessageId: string,
    ) => {
      setMeta(turn.meta);
      const activeFormKey = turn.meta.activeFormKey ?? turn.meta.formKey;
      const currentAppearance = turn.meta.appearances?.[activeFormKey];
      if (currentAppearance) session.syncAppearance(currentAppearance);

      // 新 API 直接下发本轮已入账值；旧 API 则用交流前后的服务端关系视图补算。
      // 两条路径都只展示服务端事实，不在前端猜增长规则。
      const relationshipDelta = relationshipDeltaOf(turn.meta);
      if (relationshipDelta) {
        relationshipBeforeTurn.current.delete(clientMessageId);
        if (relationshipDelta.bond !== 0 || relationshipDelta.affinity !== 0) {
          showValueChanges([
            { label: "羁绊", delta: relationshipDelta.bond },
            { label: "好感", delta: relationshipDelta.affinity },
          ]);
        }
      } else {
        const before = relationshipBeforeTurn.current.get(clientMessageId);
        if (before) {
          void fetchRelationship()
            .then((after) => {
              const fallbackDelta = relationshipDeltaBetween(before, after);
              if (fallbackDelta && (fallbackDelta.bond !== 0 || fallbackDelta.affinity !== 0)) {
                showValueChanges([
                  { label: "羁绊", delta: fallbackDelta.bond },
                  { label: "好感", delta: fallbackDelta.affinity },
                ]);
              }
            })
            .catch(() => {
              // HUD 的 reload 仍会照常执行；这里只是兼容旧响应的非关键飘窗。
            })
            .finally(() => relationshipBeforeTurn.current.delete(clientMessageId));
        }
      }

      // 服务端确认了，乐观条目退场，换成它返回的真身。
      // 这一步漏掉的话，同一句话会以两种身份同时留在屏幕上。
      //
      // 顺带清掉上一次失败时那句离线兜底话：确认回来的消息排在 `visible` 里，
      // 位置在 localEntries 之前，留着它就会出现「她说够不着」排在
      // 「她的回答」后面，读起来像是答完又反悔。
      setLocalEntries((entries) =>
        entries.filter(
          (entry) =>
            entry.message.id !== clientMessageId && entry.message.isFallback !== true,
        ),
      );

      // 只对本轮刚提交的完整歌曲尝试自动播放。enter 读到的历史消息不会触发，
      // 清唱片段是 audio part，也不会占用全局播放器。
      for (const message of turn.messages) {
        if (message.role !== "assistant") continue;
        const music = message.parts.find(
          (part): part is Extract<MessagePart, { type: "music" }> =>
            part.type === "music" && part.playable,
        );
        if (music) playMusicTrack(musicTrackOf(music));
      }

      if (turn.conversationId !== conversationId) {
        // 跨了会话边界（隔天、久别重逢），她那边已经是新的一段对话了。
        // 局部追加会把两段接在一起，整段重取。
        reloadChat();
      } else {
        setCommitted((prev) => mergeById(prev, turn.messages));
      }

      // 这一轮可能扣了灵力、涨了羁绊，左上角那块面板要跟着变。
      reloadRelationship();
    },
    [
      conversationId,
      playMusicTrack,
      reloadChat,
      reloadRelationship,
      session.syncAppearance,
      showValueChanges,
    ],
  );

  /**
   * 投递一条消息。首发与重试共用这一条路径。
   *
   * clientMessageId 由调用方给：首发生成一个新的，重试复用原来的那个。
   *
   * ## 两条传输路径，同一套善后
   *
   * 宿主能读流式响应体就走 SSE 逐字显示，否则退回整句请求—响应。
   * 判据是 `Response.prototype.body` 而不是 UA——见 `streamingSupported()`。
   * 两条路径的成功都收敛到 `commit()`，失败都收敛到 `fail()`，
   * 中间那段差异仅限于「字是怎么到屏幕上的」。
   */
  const deliver = useCallback(
    (input: ComposerMessageInput, clientMessageId: string) => {
      clearToolActivity();
      const text = input.text.trim();
      if (input.imagePreviews?.length) {
        setImagePreviewUrls((current) => {
          const next = new Map(current);
          for (const preview of input.imagePreviews ?? []) next.set(preview.assetId, preview.url);
          return next;
        });
      }
      const parts: MessagePart[] = [
        ...(input.imageAssetIds ?? []).map((assetId, index) => ({
          type: "image" as const,
          assetId,
          alt: `用户发送的图片 ${index + 1}`,
        })),
        ...(text ? [{ type: "text" as const, text }] : []),
      ];
      if (
        !relationshipBeforeTurn.current.has(clientMessageId) &&
        relationship.state.phase === "ready"
      ) {
        relationshipBeforeTurn.current.set(
          clientMessageId,
          relationshipSnapshotOf(relationship.state.data),
        );
      }

      setLocalEntries((entries) => {
        const known = entries.some(
          (entry) => entry.message.id === clientMessageId,
        );

        // 重试是原地把状态改回「正在送出」，顺便丢掉上一次的失败说明。
        // exactOptionalPropertyTypes 下不能写 failureNote: undefined，
        // 只能重建一个不含这个键的对象。
        if (known) {
          return entries.map((entry) =>
            entry.message.id === clientMessageId
              ? { message: entry.message, delivery: "sending" as const }
              : entry,
          );
        }

        return [
          ...entries,
          {
            message: {
              schemaVersion: 1,
              id: clientMessageId,
              conversationId: conversationId ?? "",
              sequence: 0,
              role: "user",
              parts,
              createdAt: new Date().toISOString(),
            },
            delivery: "sending" as const,
          },
        ];
      });

      const fail = (note: string, withFallbackLine = true) => {
        clearToolActivity();
        setStreaming(false);
        setStream(null);
        setLocalEntries((entries) =>
          entries.map((entry) =>
            entry.message.id === clientMessageId
              ? { ...entry, delivery: "failed" as const, failureNote: note }
              : entry,
          ),
        );
        if (withFallbackLine) appendOfflineLine();
      };

      /**
       * 等不到她的回答了。**不谎称「未送达」，改成整段重取。**
       *
       * `fail()` 走的是「这句话没送出去」，会给用户那条打上未送达徽标
       * 加一颗重试。但用户那条消息在**调模型之前**就已经落库了
       * （`chat.orchestrator.ts` 先 appendUserMessage 再扣费再调模型），
       * 所以那个徽标从来就是假的：下次进来那句话好端端在那儿，
       * 而且带着她的完整回答——助手消息同样是无条件落库的。
       *
       * 既然东西都在库里，正确的动作就不是让用户「重试」，
       * 而是把那一轮取回来。重试反而有害：在助手消息刚好还没落完库的
       * 那个窄窗口里重发，`deduped` 为真但查不到已有回复，会再调一次模型，
       * 同一条用户消息挂上两条回答。
       *
       * 重取失败（真的断网）会落进 `chat.state.phase === "failed"`，
       * 那里本来就有一整块离线态与重试入口，比这里再编一句要诚实。
       */
      const giveUp = (message = "等太久了，正在把她的回答取回来") => {
        clearToolActivity();
        setStreaming(false);
        setStream(null);
        showFlash(message);
        reloadChat();
      };

      /*
       * 没有会话 ID 就不发。
       *
       * 刻意不用夹具编一个顶上去：那样请求会带着一个不存在的会话打到服务端，
       * 服务端要么建出一条孤儿会话，要么返回一个让人一头雾水的 404。
       *
       * 留一点延迟是为了让「正在送出」可见——瞬间失败会让人以为按钮没响应。
       */
      if (!conversationId) {
        window.setTimeout(
          () => fail("会话还没建立，这句没能送到灵境。"),
          420,
        );
        return;
      }

      setStreaming(true);

      /* ---------------- 路径一：SSE 逐字 ---------------- */
      if (streamingSupported()) {
        /*
         * 本轮陆续确认下来的消息，攒到 `turn.done` 再一次性交给 commit。
         *
         * 逐条 setState 会让「用户那条的真身」先于「她的回答」落到屏幕上，
         * 而此刻乐观条目还在，同一句话会闪一下两份。
         */
        const received: ConversationMessage[] = [];
        let settled = false;
        let sawAssistantText = false;
        /*
         * 这一轮是被组件卸载掐掉的，不是出了错。
         *
         * 卸载的取消同样会走到 `onClose`，而那时已经没人在看这块屏幕了，
         * 再去 setState 或者补打一次 `chat/enter` 都是纯粹的浪费。
         * 光看 `controller.signal.aborted` 分不出来——看门狗也会置真。
         */
        let abandoned = false;

        const cancel = openChatStream(
          {
            clientMessageId,
            text,
            ...(input.imageAssetIds?.length ? { imageAssetIds: input.imageAssetIds } : {}),
          },
          {
            onEvent: (event) => {
              switch (event.type) {
                case "turn.started":
                  // 她要开口了。挂出一个空的流式气泡，之后 delta 往里填。
                  // id 是服务端预分配的，等 message.committed 到达时
                  // 同一个 id 自然接管，中间不会闪。
                  setStream({ messageId: event.messageId, text: "" });
                  break;
                case "turn.activity":
                  if (!sawAssistantText) showToolActivity(event.requestId, event.activity);
                  break;
                case "text.delta":
                  if (event.delta.length > 0) {
                    sawAssistantText = true;
                    clearToolActivity();
                  }
                  setStream((s) =>
                    s && s.messageId === event.messageId
                      ? { messageId: s.messageId, text: s.text + event.delta }
                      : s,
                  );
                  break;
                case "message.committed":
                  received.push(event.message);
                  break;
                case "turn.done":
                  settled = true;
                  clearToolActivity();
                  commit(
                    {
                      conversationId: event.conversationId,
                      messages: received,
                      meta: event.meta,
                    },
                    clientMessageId,
                  );
                  break;
                case "stream.error":
                  settled = true;
                  clearToolActivity();
                  // 文字已经从服务端流出来，说明用户消息送达且模型也回答了；
                  // 此后的错误只可能发生在落库/结算/连接收尾。此时标「未送达」
                  // 会同时抹掉刚显示的回复，改为重取服务端权威状态。
                  if (event.partial || sawAssistantText) {
                    giveUp("她已经答完，正在确认这条回复的保存状态");
                    break;
                  }
                  /*
                   * 一个字都没收到才是真正的发送失败；这时保留原有重试入口。
                   */
                  fail(
                    event.retryable
                      ? `${event.message}。重试会复用同一个幂等键，不会重复扣灵力。`
                      : event.message,
                    true,
                  );
                  break;
                default:
                  break;
              }
            },
            onError: (cause) => {
              settled = true;
              clearToolActivity();
              if (sawAssistantText) {
                giveUp("连接在收尾时中断，正在把她的回答取回来");
                return;
              }
              // 空闲看门狗：连服务端的错误帧都送不过来，也就是连接本身没了。
              // 那一轮在服务端多半跑完并落库了，取回来而不是标未送达。
              if (cause.code === STREAM_STALLED) {
                giveUp();
                return;
              }
              fail(`${cause.message}。重试会复用同一个幂等键，不会重复扣灵力。`);
            },
            onClose: () => {
              inflight.current = null;
              clearToolActivity();
              setStreaming(false);
              setStream(null);
              /*
               * 流关了却既没收到 turn.done 也没收到错误帧。
               *
               * 「停止」按钮删掉之后，走到这里只剩一种来源：服务端在帧发完
               * 之前把连接关了。与卡死同解——她那一轮已经落库，取回来就是。
               */
              if (!settled && !abandoned) giveUp();
            },
          },
        );
        inflight.current = () => {
          abandoned = true;
          cancel();
        };
        return;
      }

      /* ---------------- 路径二：整句请求—响应 ---------------- */
      const controller = new AbortController();
      inflight.current = () => controller.abort();

      sendChatMessage(
        {
          clientMessageId,
          text,
          ...(input.imageAssetIds?.length ? { imageAssetIds: input.imageAssetIds } : {}),
        },
        controller.signal,
      )
        .then((turn) => {
          inflight.current = null;
          setStreaming(false);
          commit(turn, clientMessageId);
        })
        .catch((cause: unknown) => {
          inflight.current = null;
          if (controller.signal.aborted) {
            // 组件卸载。没人在看了，什么都别做。
            return;
          }
          const failure = cause instanceof ApiError ? cause : null;
          // `chat-api.ts` 的 45 秒超时。服务端那一轮没有任何 abort 接线
          // （`chat/messages` 不接 `req.on("close")`），一定跑完并落库，
          // 与流式看门狗同解。
          if (failure?.kind === "timeout") {
            giveUp();
            return;
          }
          fail(
            failure
              ? `${failure.message}。重试会复用同一个幂等键，不会重复扣灵力。`
              : "没送出去，原因不明。",
          );
        });
    },
    [
      appendOfflineLine,
      clearToolActivity,
      commit,
      conversationId,
      relationship.state,
      reloadChat,
      showFlash,
      showToolActivity,
    ],
  );

  const send = useCallback(
    (input: ComposerMessageInput) => deliver(input, newClientMessageId()),
    [deliver],
  );

  /**
   * 填完称呼。
   *
   * 两步，顺序不能反：
   *
   *   ① `POST me/preferred-name` 把名字落库，并补上那个空着的账号昵称
   *   ② 替用户说一句「叫我 xx 就好」，她照常回一句
   *
   * ## 为什么要补发那一句
   *
   * 她刚问完「我该怎么称呼你呀」。用户在框里填了名字，如果对话就此静默，
   * 那一问一答只发生在数据库里——**屏幕上她的问题永远悬着没被回答**，
   * 下一句她还得自己另起一个话头。补上这句让对话闭环，
   * 而且她的回复此时已经能用上新称呼（①已经落库，L1 提示词下一轮就读得到）。
   *
   * 措辞是「叫我 xx 就好」而不是「我的名字是 xx」：**它得读起来像一句
   * 日常对话**，因为它会作为用户消息永久留在聊天记录里，也会进后续几轮的
   * 历史窗口。一句像表单回执的话会让整段对话显得不像在聊天。
   *
   * ## 这一轮照常扣灵力
   *
   * 它是用户主动说的一句话，走的是和手打完全相同的路径。
   * **不要为它开后门**——「开场白与续场由平台承担」的边界是
   * 「服务端自己决定开口的那些轮次」，这一轮是用户开的口。
   *
   * ## ①失败就不发②
   *
   * 名字没存上还替用户宣告「叫我 xx 就好」，她下一句多半还是叫不出来，
   * 用户会以为她没记住。失败时留在框里显示原因，让人重试——
   * 这个框不可跳过，所以**必须**留得住重试，不能让人卡死在这儿。
   */
  const submitName = useCallback(
    (name: string) => {
      setNameSubmitting(true);
      setNameError(null);
      void apiRequest("/api/v1/app/me/preferred-name", {
        method: "POST",
        // 这个名字同时补上账号昵称——那一刻用户给的是「我叫什么」，
        // 而新账号的昵称本来就是空的。服务端只在昵称为空时才写，
        // 不会冲掉迁移进来的 1,495 个真实昵称。
        body: { name, alsoSetDisplayName: true },
      })
        .then(() => {
          setNameSubmitting(false);
          // 收起输入框，把「文字回复」/ 输入框让回来。
          setNameDone(true);
          // 重新取一次：她之后的回复会用上这个称呼，余额面板也要同步。
          reloadRelationship();
          send({ text: `叫我${name}就好` });
        })
        .catch((e: unknown) => {
          setNameSubmitting(false);
          setNameError(
            e instanceof ApiError ? e.message : "没能记下来，等下再试试",
          );
        });
    },
    [reloadRelationship, send],
  );

  /**
   * 重试。
   *
   * **复用原来的 clientMessageId。** 这是幂等键：重试若换一个新键，服务端
   * 会把它当成一条全新的消息，超时那次万一其实已经落库，就会出现两条重复
   * 消息和两次扣费。所以这里是原地重发，不是删掉再发一条。
   */
  const retry = useCallback(
    (clientMessageId: string) => {
      const target = localEntries.find(
        (entry) => entry.message.id === clientMessageId,
      );
      if (!target) return;
      const text = target.message.parts.find(
        (part): part is Extract<MessagePart, { type: "text" }> => part.type === "text",
      );
      const images = target.message.parts.filter(
        (part): part is Extract<MessagePart, { type: "image" }> => part.type === "image",
      );
      if (!text && images.length === 0) return;
      deliver(
        {
          text: text?.text ?? "",
          ...(images.length > 0 ? { imageAssetIds: images.map((image) => image.assetId) } : {}),
        },
        clientMessageId,
      );
    },
    [deliver, localEntries],
  );

  /* -------------------------------------------------------------------- */
  /* 操作条上的四个动作                                                    */
  /* -------------------------------------------------------------------- */

  /**
   * 复制。
   *
   * 优先 `navigator.clipboard`，失败回落到一个离屏 textarea + `execCommand`。
   * 回落不是为了老浏览器，是为了**非安全上下文**：`clipboard` 在 http 页面上
   * 整个不存在，而微信里打开的调试地址常常就是 http。
   */
  const copyText = useCallback(
    (text: string) => {
      const legacy = (): boolean => {
        try {
          const area = document.createElement("textarea");
          area.value = text;
          // 不能用 display:none —— 那样选不中，execCommand 会静默失败。
          area.style.cssText = "position:fixed;top:-9999px;opacity:0";
          document.body.appendChild(area);
          area.select();
          const ok = document.execCommand("copy");
          document.body.removeChild(area);
          return ok;
        } catch {
          return false;
        }
      };

      const done = () => showFlash("已经抄下来了");
      const failed = () => showFlash("没能复制，长按选中试试");

      if (navigator.clipboard?.writeText) {
        void navigator.clipboard
          .writeText(text)
          .then(done)
          .catch(() => (legacy() ? done() : failed()));
        return;
      }
      if (legacy()) done();
      else failed();
    },
    [showFlash],
  );

  /**
   * 点赞 / 点踩。**再点同一个就是收回。**
   *
   * 乐观更新：按钮立刻亮起来，失败再退回去。这一步没有任何副作用值得等
   * 一个来回——它不扣钱、不动关系值，最坏的结果是一行训练信号没写上。
   */
  const rate = useCallback(
    (messageId: string, next: 1 | -1) => {
      const current = ratings.get(messageId) ?? null;
      const value = current === next ? null : next;
      const previous = current;
      setRatings((m) => new Map(m).set(messageId, value));
      void rateMessage(messageId, value).catch((e: unknown) => {
        setRatings((m) => new Map(m).set(messageId, previous));
        showFlash(e instanceof ApiError ? e.message : "这次评价没记上");
      });
    },
    [ratings, showFlash],
  );

  /**
   * 删除。
   *
   * **不弹二次确认。** 删的是一句话不是账号，而确认框会把一个轻动作变成
   * 两步操作；真正的保护是「她会说出来」——飘窗那句
   * 「好像忘记了什么东西……」既是回执也是提醒：这句话连同她的记忆一起没了。
   *
   * 与复制、评价不同，这里**不做乐观删除**：万一服务端拒绝（不是你的消息、
   * 网络断了），已经从屏幕上消失的那句话再变回来会非常突兀。
   */
  const removeMessage = useCallback(
    (messageId: string) => {
      void deleteMessage(messageId)
        .then(() => {
          setDeletedIds((s) => new Set(s).add(messageId));
          setOpenActionsId((id) => (id === messageId ? null : id));
          showFlash("好像忘记了什么东西……");
        })
        .catch((e: unknown) => {
          showFlash(e instanceof ApiError ? e.message : "这句话没能删掉");
        });
    },
    [showFlash],
  );

  /** 一条消息要挂的全部操作。两处渲染共用，免得漏掉其中一个。 */
  const actionsFor = useCallback(
    (message: ConversationMessage) => ({
      open: openActionsId === message.id,
      onToggleOpen: () =>
        setOpenActionsId((id) => (id === message.id ? null : message.id)),
      onCopy: copyText,
      onDelete: () => removeMessage(message.id),
      rating: ratings.get(message.id) ?? null,
      onRate: (next: 1 | -1) => rate(message.id, next),
      ...(speak.supported
        ? {
            speakState: speak.stateOf(message.id),
            onToggleSpeak: () => requestSpeak(message.id),
          }
        : {}),
    }),
    [copyText, openActionsId, rate, ratings, removeMessage, requestSpeak, speak],
  );

  // 删掉的那些立刻从视图里滤掉。服务端是软删，`visible` 那份数据要等
  // 下一次 enter 才会更新——不在这里滤一遍的话，用户点了删除，
  // 那句话还在屏幕上，他会再点一次。
  const visible = mergeById(serverMessages, committed).filter(
    (m) => !deletedIds.has(m.id),
  );
  const displayedMessages = handledPaintConfirmationId
    ? visible.filter(
        (message) =>
          !message.parts.some(
            (part) =>
              part.type === "skill_progress" &&
              part.status === "awaiting_confirmation" &&
              part.executionId === handledPaintConfirmationId,
          ),
      )
    : visible;
  const displayedTimeline = groupChannelEncounters(displayedMessages);
  const currentForm = meta?.activeFormKey ?? meta?.formKey ?? "normal";
  const currentAppearance = appearanceForForm(meta, currentForm, appearance);

  const renderPersistedMessage = (message: ConversationMessage) => {
    if (message.role === "system") {
      return <SystemEventBar key={message.id} message={message} />;
    }
    const messageAppearance = appearanceForForm(
      meta,
      messageFormKey(message, currentForm),
      appearance,
    );
    return (
      <MessageBubble
        key={message.id}
        entry={{ message }}
        showAiGeneratedMark={aiDisclosureEnabled}
        speakerName={messageAppearance.name}
        avatarUrl={messageAppearance.avatarUrl}
        onNameClick={() => setFormSheetOpen(true)}
        imagePreviewUrls={imagePreviewUrls}
        onImagePreviewConsumed={consumeImagePreview}
        onRetrySkill={retryPaint}
        retryingSkillExecutionId={retryingPaintExecutionId}
        {...actionsFor(message)}
      />
    );
  };

  const renderEncounter = (
    encounter: Extract<ChatTimelineItem, { kind: "channel_encounter" }>,
  ) => (
    <ChannelEncounter
      key={encounter.id}
      endpointLabel={encounter.endpointLabel}
      messages={encounter.messages}
    >
      {encounter.messages.map(renderPersistedMessage)}
    </ChannelEncounter>
  );

  // 服务端每轮都会给出 normal / weakened 两套外观。提前把两套立绘和头像送进
  // 浏览器缓存，避免灵力归零后才开始下载 Q 版大图，期间舞台长时间空白。
  useEffect(() => {
    const appearances = meta?.appearances;
    if (!appearances) return;

    const urls = new Set<string>();
    for (const formAppearance of Object.values(appearances)) {
      if (!formAppearance) continue;
      urls.add(formAppearance.defaultPortraitUrl);
      urls.add(formAppearance.avatarUrl);
      for (const url of Object.values(formAppearance.portraits)) urls.add(url);
    }

    for (const url of urls) {
      if (!url || appearancePreloadsRef.current.has(url)) continue;
      const image = new Image();
      image.decoding = "async";
      image.src = url;
      appearancePreloadsRef.current.set(url, image);
    }
  }, [meta?.appearances]);
  const paintProgressParts = visible.flatMap((message) =>
    message.parts.filter(
      (part): part is SkillProgressPart =>
        part.type === "skill_progress" && part.skillKey === "dream.paint.pro",
    ),
  );
  const awaitingPaintConfirmation = [...paintProgressParts]
    .reverse()
    .find((part) => part.status === "awaiting_confirmation") ?? null;
  const runningPaintExecution = [...paintProgressParts]
    .reverse()
    .find((part) => part.status === "queued" || part.status === "running") ?? null;
  const pollPaintExecutionId =
    activePaintExecutionId ?? runningPaintExecution?.executionId ?? null;

  const decidePaint = useCallback(
    async (decision: "approve" | "reject") => {
      const executionId = awaitingPaintConfirmation?.executionId;
      if (!executionId || paintDecisionPending) return;
      // “不画了”是无需等待反馈的关闭动作：先把确认框和占位消息一起收起。
      if (decision === "reject") setHandledPaintConfirmationId(executionId);
      setPaintDecisionPending(true);
      setPaintDecisionError(null);
      try {
        const execution = await decidePaintExecution(executionId, decision);
        setHandledPaintConfirmationId(executionId);
        if (execution.status === "queued" || execution.status === "running") {
          setActivePaintExecutionId(executionId);
        }
        // 生图状态同步时保留当前消息 DOM；否则已加载的旧图会被卸载后重新请求。
        refreshChat();
        reloadRelationship();
      } catch (cause) {
        if (decision === "reject") setHandledPaintConfirmationId(null);
        setPaintDecisionError(
          cause instanceof ApiError ? cause.message : "这次选择没有送到，请再试一次",
        );
      } finally {
        setPaintDecisionPending(false);
      }
    }, [
      awaitingPaintConfirmation?.executionId,
      paintDecisionPending,
      refreshChat,
      reloadRelationship,
    ],
  );

  const retryPaint = useCallback(
    async (sourceExecutionId: string) => {
      if (retryingPaintExecutionId) return;
      setRetryingPaintExecutionId(sourceExecutionId);
      try {
        const execution = await retryPaintExecution(sourceExecutionId);
        if (execution.status === "queued" || execution.status === "running") {
          // 重试会派生一个新 execution；轮询必须跟随服务端返回的新 id，
          // 不能继续盯着旧失败卡片的 id。
          setActivePaintExecutionId(execution.id);
        }
        refreshChat();
        reloadRelationship();
      } catch (cause) {
        showFlash(
          cause instanceof ApiError ? cause.message : "这次没能重新开始画，请再试一次",
        );
      } finally {
        setRetryingPaintExecutionId(null);
      }
    }, [refreshChat, reloadRelationship, retryingPaintExecutionId, showFlash],
  );

  useEffect(() => {
    if (!pollPaintExecutionId) return;
    let stopped = false;
    let timer: number | null = null;
    const controller = new AbortController();

    const poll = async () => {
      try {
        const execution = await fetchSkillExecution(
          pollPaintExecutionId,
          controller.signal,
        );
        if (stopped) return;
        if (
          execution.status === "succeeded" ||
          execution.status === "failed" ||
          execution.status === "cancelled"
        ) {
          setActivePaintExecutionId(null);
          refreshChat();
          reloadRelationship();
          return;
        }
      } catch (cause) {
        if (stopped || controller.signal.aborted) return;
        console.warn("读取画画任务进度失败", cause);
      }
      if (!stopped) timer = window.setTimeout(poll, 2_500);
    };

    void poll();
    return () => {
      stopped = true;
      controller.abort();
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [pollPaintExecutionId, refreshChat, reloadRelationship]);

  const inputMaxLength = meta?.inputMaxLength ?? null;
  const imagePowerSurcharge = Number.isFinite(meta?.imagePowerSurcharge)
    ? (meta?.imagePowerSurcharge ?? null)
    : null;
  const imageMaxCount =
    Number.isInteger(meta?.imageMaxCount) && (meta?.imageMaxCount ?? 0) > 0
      ? (meta?.imageMaxCount ?? 1)
      : 1;

  /**
   * 流式占位气泡。
   *
   * `turn.started` 刚到、首个字还没来时只显示三点输入指示，不画空白消息气泡；
   * 否则同一轮会同时出现一颗空白气泡和一颗三点气泡。
   *
   * ⚠ **落库的真身一旦进 `visible` 就立刻让位**，判据是 id 相同。
   * 不加这道判断的话，`turn.done` 到 `onClose` 之间会有一帧同时画着
   * 两条一模一样的回复。靠「先清空 stream 再 setCommitted」也能躲开，
   * 但那依赖两次 setState 的先后顺序，是个一改就碎的约定。
  */
  const streamEntry: ChatEntry | null =
    stream !== null &&
    stream.text.length > 0 &&
    !visible.some((m) => m.id === stream.messageId)
      ? {
          message: {
            schemaVersion: 1,
            id: stream.messageId,
            conversationId: conversationId ?? "",
            sequence: Number.MAX_SAFE_INTEGER,
            role: "assistant",
            parts: [{ type: "text", text: stream.text }],
            createdAt: new Date().toISOString(),
          },
        }
      : null;
  // 两种对话模式共用的舞台都展示她此刻的形态。历史台词仍在气泡模式里按 Message.formKey
  // 显示自己的头像和名字，但不能让上一条普通消息把已经 weakened 的舞台锁住。
  // 对话页卸载时清掉覆盖，其他页面继续使用全站当前形态。
  useLayoutEffect(() => {
    setStagePortrait({
      name: currentAppearance.name,
      portraitUrl: currentAppearance.portraitUrl,
      allowLocalFallback: currentForm === "normal",
    });
  }, [
    currentAppearance.name,
    currentAppearance.portraitUrl,
    currentForm,
    setStagePortrait,
  ]);

  // 清空只应该发生在离开聊天页时。不能放进上面的更新 effect cleanup：
  // 形态或 URL 每变一次 cleanup 都会先写 null，造成舞台在两套立绘之间短暂卸载。
  useLayoutEffect(
    () => () => setStagePortrait(null),
    [setStagePortrait],
  );

  /**
   * 三点跳动只在**一个字都还没来**的时候画。
   *
   * 字开始往外吐之后还留着它，屏幕上就是「她在打字」和「她说的话」并存，
   * 而她显然已经在说了。
   */
  const awaitingFirstToken =
    (streaming && !stream?.text) || (topupThankPending && !streaming);

  /*
   * 停在最新一条。
   *
   * 这里直接改消息容器的 scrollTop，不再让末尾锚点 scrollIntoView：流式文字会高频
   * 增长，反复重启 smooth 动画后，回复收尾时「流式占位 → 落库真身」的布局切换会
   * 把动画停在半路，看起来就像对话框忽然没有拉到底。useLayoutEffect 保证每次
   * 布局提交后、浏览器绘制前一次到位。
   *
   * 对话模块的默认语义就是展示最新一条；用户仍然可以在没有新消息和布局变化时
   * 上翻历史，但回复、刷新或上下栏尺寸变化后都重新落到底部。
   */
  const pinLatest = useCallback(() => {
    const viewport = streamViewportRef.current;
    if (!viewport || historyPrependingRef.current) return;
    viewport.scrollTop = viewport.scrollHeight;
  }, []);

  useLayoutEffect(() => {
    if (mode !== "bubble") return;
    pinLatest();
  }, [
    localEntries,
    visible.length,
    streaming,
    stream?.text,
    topupThankPending,
    chat.state.phase,
    mode,
    pinLatest,
  ]);

  /*
   * prepend 之后保持视口：新增内容有多高，就把 scrollTop 补回多高。
   *
   * 不能只记第一条消息 id 再 scrollIntoView；用提交后的真实 DOM 高度差
   * 还原视口，不依赖消息气泡的预估高度。
   * ref 留到下一帧才释放，是为了让同一轮 Mutation/ResizeObserver 看见
   * “正在补历史”，不要紧接着又执行 pinLatest 把人拉回底部。
   */
  useLayoutEffect(() => {
    const snapshot = historyPrependSnapshotRef.current;
    const viewport = streamViewportRef.current;
    if (!snapshot || !viewport) return;

    viewport.scrollTop =
      snapshot.scrollTop + Math.max(0, viewport.scrollHeight - snapshot.scrollHeight);
    historyPrependSnapshotRef.current = null;

    if (historyPrependReleaseFrameRef.current !== null) {
      window.cancelAnimationFrame(historyPrependReleaseFrameRef.current);
    }
    historyPrependReleaseFrameRef.current = window.requestAnimationFrame(() => {
      historyPrependingRef.current = false;
      historyPrependReleaseFrameRef.current = null;
    });
  }, [earlierMessages]);

  useEffect(
    () => () => {
      if (historyPrependReleaseFrameRef.current !== null) {
        window.cancelAnimationFrame(historyPrependReleaseFrameRef.current);
      }
    },
    [],
  );

  /*
   * 输入框不在消息滚动容器里面，而是它下面的 flex 兄弟节点。textarea 自适应高度、
   * 图片预览、错误提示或安全区变化都会改变 dock 高度，继而压缩 / 放大消息视口；
   * 这些变化可能只发生在 Composer 内部，ChatPage 不一定重新渲染。
   *
   * 因此额外观察视口、输入区和每一条消息的真实尺寸。输入区占完自己的高度、
   * 图片加载完成或操作条撑高气泡后再重新贴底，消息的最后一行就始终停在输入框
   * 上沿，而不会被高度差顶出去。
   *
   * ⚠ 这里不能再根据 onScroll 推断用户是否主动上翻。HUD 从 loading 恢复到 ready
   * 时会把消息视口压矮，浏览器也会因此派发 scroll；那不是用户操作。此前正是把
   * 这次程序性事件误判成上翻，导致 ResizeObserver 随后拒绝贴底，刷新与数值飘窗
   * 后都会稳定少滚一个 HUD 的高度。
   */
  useEffect(() => {
    if (mode !== "bubble" || typeof ResizeObserver === "undefined") return;
    const viewport = streamViewportRef.current;
    const dock = dockRef.current;
    if (!viewport || !dock) return;

    let frame: number | null = null;
    const schedulePin = () => {
      if (historyPrependingRef.current) return;
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        frame = null;
        pinLatest();
      });
    };
    const observer = new ResizeObserver(schedulePin);
    observer.observe(viewport);
    observer.observe(dock);

    // scrollHeight 变化不会改变 viewport 自己的边框尺寸，所以只观察 viewport
    // 抓不到图片加载、操作条展开等内容增高。逐个观察直接子项；新增消息由
    // MutationObserver 接进同一个 ResizeObserver。
    const observedChildren = new Set<Element>();
    const observeChildren = () => {
      for (const child of observedChildren) {
        if (child.parentElement === viewport) continue;
        observer.unobserve(child);
        observedChildren.delete(child);
      }
      for (const child of viewport.children) {
        if (observedChildren.has(child)) continue;
        observedChildren.add(child);
        observer.observe(child);
      }
    };
    observeChildren();
    const mutationObserver = new MutationObserver(() => {
      observeChildren();
      schedulePin();
    });
    mutationObserver.observe(viewport, { childList: true });

    return () => {
      mutationObserver.disconnect();
      observer.disconnect();
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [mode, pinLatest]);

  /*
   * 能不能发话，取决于有没有真实会话。
   *
   * 发不出去就不画输入区——旧版未登录时也是把输入框整个换掉，而不是留一个
   * 点了就报错的框。连不上灵境的时候屏幕上只剩缈缈和她说的那句话。
   */
  const canSend = conversationId !== null;

  const composer = (
    <Composer
      maxLength={inputMaxLength}
      imagePowerSurcharge={imagePowerSurcharge}
      imageMaxCount={imageMaxCount}
      imageNoticeAcknowledged={costNotices?.chatImagePower ?? null}
      onAcknowledgeImageNotice={() => rememberCostNotice("chat_image_power")}
      streaming={streaming || topupThankPending}
      allowMedia={(meta?.activeFormKey ?? meta?.formKey) === "normal"}
      onSend={send}
    />
  );

  /**
   * 舞台模式弹窗里的输入区。
   *
   * 与上面那个的唯一差别：**送出后立刻收起弹窗**。
   *
   * 弹窗是个盖住整块屏幕的遮罩，而她的回答画在下面的对话框里。不收起的话，
   * 用户送出后盯着的是一个空输入框，她说了什么全被遮住——得先自己点一次
   * 关闭才看得到，而那一步没有任何东西提示他要做。接上流式之后更明显：
   * 字正在一个个往外冒，而用户看不见。
   */
  const replyComposer = (
    <Composer
      maxLength={inputMaxLength}
      imagePowerSurcharge={imagePowerSurcharge}
      imageMaxCount={imageMaxCount}
      imageNoticeAcknowledged={costNotices?.chatImagePower ?? null}
      onAcknowledgeImageNotice={() => rememberCostNotice("chat_image_power")}
      streaming={streaming || topupThankPending}
      allowMedia={(meta?.activeFormKey ?? meta?.formKey) === "normal"}
      onSend={(input) => {
        setReplyOpen(false);
        send(input);
      }}
    />
  );

  /*
   * 降级公告。
   *
   * `FORM_SWITCH.requiresVisibleAnnouncement` 要求自动降级必须让用户看见，
   * 禁止静默切换——不然用户只会觉得「她今天怎么变笨了」。
   */
  const downgraded =
    (meta?.activeFormReason ?? meta?.formReason) === "auto_downgrade";

  /**
   * 「怎么称呼你」输入框。
   *
   * ⚠ **不是弹层，不在她开口之前出现，也不能跳过。**
   *
   * 它曾经是整屏遮罩 + 居中卡片，新用户一进来就糊在脸上——而那句
   * 「我该怎么称呼你呀」本来是**她开场白的最后一句**。开场白还没读到，
   * 框先出现了，于是它看起来像个凭空冒出来的注册表单。
   *
   * 现在两种模式下都是一块停在输入口上方的行内卡片，且都要等她说完：
   *   舞台模式  交给 TheaterView 的 atEndSlot，翻到最后一页才画，并顶掉「文字回复」
   *   气泡模式  整段开场白一次就铺完，所以不需要额外门禁，直接顶掉输入框
   *
   * 顶掉输入口就是「强制」的实现方式：没有遮罩，但填完之前发不出任何消息。
   * 出现条件是服务端的 `mustAskPreferredName`（昵称与称呼都空），
   * 填完之后两者同时有值，这个框一辈子只会见到一次。
   *
   * 两种模式都要渲染。它曾经只写在气泡模式那个 return 里，而默认模式是舞台
   * （`App.tsx` 的 `MODE_KEY` 缺省值就是 theater），结果新用户根本看不到它。
   */
  const namePrompt = askName ? (
    <PreferredNamePrompt
      characterName={currentAppearance.name}
      onSubmit={submitName}
      submitting={nameSubmitting}
      error={nameError}
    />
  ) : null;

  const formSheet = (
    <Sheet
      open={formSheetOpen}
      title="她现在是什么状态"
      onClose={() => setFormSheetOpen(false)}
    >
      <div className="prose">
        <p>
          缈缈有两种形态。灵力充足时是<strong>缈缈</strong>；灵力耗尽会自动变成
          <strong>笨笨缈缈</strong>——反应短一些、迷糊一些，但
          <strong>不消耗灵力，陪伴不会中断</strong>。
        </p>
        <p>
          两个形态共享同一份记忆，羁绊与好感照常累积，
          <strong>不会因为降级而丢失或归零</strong>。它是一种状态，不是故障。
        </p>
        {meta === null ? (
          <p>
            当前形态由服务端根据灵力余额裁定，前端不声明也不猜测。
            现在连不上灵境，所以这里显示不出她处于哪种形态。
          </p>
        ) : (
          <p>
            她现在是
            <strong>{currentForm === "weakened" ? "笨笨缈缈" : "缈缈"}</strong>
            {downgraded ? "，因为灵力用完了自动变的" : ""}。 剩余灵力{" "}
            <strong>{meta.powerBalance}</strong> 点。
          </p>
        )}
      </div>

      <div className="form-switch-blocked">
        <IconPower size={16} />
        <div>
          <strong>手动切换暂时不可用</strong>
          <p>
            切换要向服务端发一个 <code>character.switchForm</code>{" "}
            动作并等待确认，那条路径还没实现。
          </p>
        </div>
      </div>
    </Sheet>
  );

  const paintPowerCost = awaitingPaintConfirmation?.estimatedPowerCost;
  const paintConfirmation = (
    <Sheet
      open={
        awaitingPaintConfirmation !== null &&
        handledPaintConfirmationId !== awaitingPaintConfirmation.executionId
      }
      title={`让${currentAppearance.name}开始画吗？`}
      onClose={() => void decidePaint("reject")}
    >
      <div className="paint-confirm">
        <p>
          {paintPowerCost === undefined
            ? "确认后才会开始画画并消耗灵力。"
            : `这次会消耗 ${paintPowerCost} 点灵力，确认后才会开始画画。`}
        </p>
        <p className="paint-confirm__hint">选择不画不会开始生成，也不会扣除画画灵力。</p>
        {paintDecisionError ? (
          <p className="paint-confirm__error" role="alert">{paintDecisionError}</p>
        ) : null}
        <div className="paint-confirm__actions">
          <Button
            tone="quiet"
            disabled={paintDecisionPending}
            onClick={() => void decidePaint("reject")}
          >
            不画了
          </Button>
          <Button
            tone="primary"
            disabled={paintDecisionPending}
            onClick={() => void decidePaint("approve")}
          >
            {paintDecisionPending
              ? "正在确认…"
              : paintPowerCost === undefined
                ? "确认开始画"
                : `消耗 ${paintPowerCost} 灵力，开始画`}
          </Button>
        </div>
      </div>
    </Sheet>
  );

  const voiceCostNotice = (
    <PowerCostNotice
      open={pendingSpeakId !== null}
      onClose={() => setPendingSpeakId(null)}
      onConfirm={() => {
        const messageId = pendingSpeakId;
        rememberCostNotice("message_voice_power");
        setPendingSpeakId(null);
        // 确认按钮本身是用户手势，直接从这里启动音频，兼容 iOS 的播放限制。
        if (messageId) speak.toggle(messageId);
      }}
    >
      每条消息第一次播放语音会消耗 {TTS_POWER_COST} 点灵力哦～，之后重复播放这条语音不会再消耗。
    </PowerCostNotice>
  );

  /* ---------------------------------------------------------------------- */
  /* 视觉小说模式                                                            */
  /* ---------------------------------------------------------------------- */

  if (mode === "theater") {
    // 最新一条非用户消息。旧版 InteractionChat 就是这个取法。
    // 正在流式长出来的那条排在最后——它就是此刻该显示的那句。
    const spoken = [
      ...displayedMessages,
      ...localEntries.map((e) => e.message),
      ...(streamEntry ? [streamEntry.message] : []),
    ].filter((m) => m.role === "assistant" && m.origin?.kind !== "channel");
    const latest = spoken[spoken.length - 1];
    const timelineTail = displayedTimeline[displayedTimeline.length - 1];
    const itemBeforeTail = displayedTimeline[displayedTimeline.length - 2];
    /*
     * 舞台模式不展示整条历史，只展示当前台词。因此这里只带出时间线末端的相遇：
     * 相遇仍在末端，或它后面紧接着本次站内开场白。再发生一轮站内交流后便不重复出现。
     */
    const currentEncounter =
      timelineTail?.kind === "channel_encounter"
        ? timelineTail
        : timelineTail?.kind === "message" &&
            timelineTail.message.role === "assistant" &&
            timelineTail.message.origin?.kind !== "channel" &&
            itemBeforeTail?.kind === "channel_encounter"
          ? itemBeforeTail
          : null;
    // sequence 只在 conversation 内递增；今天时间线可能包含多段 conversation，
    // 因此按已经排好序的数组位置判断“最后一条用户消息之后”，不能比 sequence。
    const latestUserIndex = displayedMessages.reduce(
      (latestIndex, message, index) => (message.role === "user" ? index : latestIndex),
      -1,
    );
    const recentSystemEvent = displayedMessages
      .slice(latestUserIndex + 1)
      .reverse()
      .find(
        (message) =>
          message.role === "system" && message.systemEvent !== undefined,
      );

    const failure = chat.state.phase === "failed" ? chat.state.error : null;

    /*
     * 这里**不传 onRetry**。
     *
     * 「再试一次」改由 TheaterView 画在 footer 的动作位上，完整理由写在那边。
     * 一句话版本：正文区是个 overflow:hidden 的翻页视口，按钮放进去会被裁掉，
     * 点它还会连带翻页。这里只留台词。
     *
     * MiaoNotice 是通用组件，别的页面照旧自己传 onRetry —— 不要为了这一处去
     * 改组件本身。
     */
    const body = failure ? (
      <MiaoNotice bare scene="conversation" error={failure} />
    ) : latest ? (
      <MessagePartsRenderer parts={latest.parts} />
    ) : null;

    /*
     * 翻页只在**换了一条消息**时归零。
     *
     * 所以这个键必须是消息身份，不能用正文内容：流式下 latest.parts 每来
     * 一个 token 就变一次，用内容做键会不停跳回第一页——旧版 MessagePlayer 的
     * `useEffect(..., [text])` 正是栽在这里。
     *
     * 流式占位气泡用的是服务端预分配的 id，落库之后 id 不变，
     * 所以从「正在说」到「说完了」这一刻也不会跳页。
     */
    const contentKey = failure
      ? `failed:${failure.requestId}`
      : (latest?.id ?? "empty");

    /*
     * 她这条能不能朗读。判据与气泡模式的 `speakable` 一致：
     * 得是她说的、已经落库的。**流式还没落库的那条不能读**——
     * 合成要拿 messageId 做幂等键，此刻服务端还没有对应的 Message 行。
     * 兜底句可以读（2026-08-01 起，两处判据一起放开的）。
     */
    const speakableId =
      !failure &&
      latest &&
      latest.role === "assistant" &&
      latest.origin?.kind !== "channel" &&
      latest.id !== stream?.messageId
        ? latest.id
        : null;

    return (
      <section className="chat chat--theater" aria-label="对话">
        {/* 朗读失败时只说一句就退场，不占据版面也不需要用户手动关。 */}
        {speakNotice ? (
          <p className="chat__speak-notice fade-in" role="status">
            {speakNotice}
          </p>
        ) : null}

        {recentSystemEvent ? <SystemEventBar message={recentSystemEvent} /> : null}

        {currentEncounter ? renderEncounter(currentEncounter) : null}

        <TheaterView
          name={currentAppearance.name}
          showAiGeneratedMark={
            aiDisclosureEnabled &&
            Boolean(latest) &&
            latest?.isFallback !== true &&
            !failure
          }
          {...(latest?.origin?.kind === "channel"
            ? {
                sourceLabel:
                  `${latest.origin.endpointLabel} · ${formatTime(latest.createdAt)}`,
              }
            : {})}
          body={body}
          contentKey={contentKey}
          /*
           * 只有「一个字都还没来」才画那个三点动画。
           * 开始吐字之后 pending 必须变回 false，否则正文区一直是动画，
           * 逐字效果等于没有；而且 `atEnd` 会被它按住，读完也不给回复口。
           */
          pending={awaitingFirstToken || chat.state.phase === "loading"}
          pendingText={awaitingFirstToken ? toolActivityText : null}
          streaming={streamEntry !== null && streaming}
          onNameClick={() => setFormSheetOpen(true)}
          {...(speak.supported && speakableId
            ? {
                speakState: speak.stateOf(speakableId),
                onToggleSpeak: () => requestSpeak(speakableId),
              }
            : {})}
          /*
           * 会话拉不回来就不给「文字回复」。新消息接在哪条 sequence 后面
           * 都说不清楚，而且没有 conversationId 本来也发不出去。
           */
          onReply={
            canSend && !failure && !topupThankPending
              ? () => setReplyOpen(true)
              : undefined
          }
          /*
           * retryable 为假就不给按钮（4xx：路由不存在、参数不合法……）。判断口径
           * 与 MiaoNotice 内部一致，都是 ApiError.retryable。
           *
           * 那种情况下 footer 右侧是空的，这是有意的：没有出路就不摆按钮，
           * 免得让人白点一次。
           */
          onRetry={failure?.retryable ? reloadChat : undefined}
          /*
           * 称呼框。TheaterView 只在**翻到最后一页**时才画它，并顺手顶掉
           * 「文字回复」——她刚问完「我该怎么称呼你呀」，此刻该回答的就是这个。
           *
           * 失败时不问：她那句话都没送到，问名字很怪。
           */
          atEndSlot={failure ? undefined : namePrompt}
        />

        <Sheet
          open={replyOpen}
          title="文字回复"
          headerDivider={false}
          onClose={() => setReplyOpen(false)}
        >
          <div className="theater__composer">{replyComposer}</div>
        </Sheet>

        {voiceCostNotice}

        {paintConfirmation}

        {formSheet}
      </section>
    );
  }

  /* ---------------------------------------------------------------------- */
  /* 气泡模式                                                                */
  /* ---------------------------------------------------------------------- */

  return (
    <section className="chat" aria-label="对话">
      {/* 朗读失败时只说一句就退场，不占据版面也不需要用户手动关。 */}
      {speakNotice ? (
        <p className="chat__speak-notice fade-in" role="status">
          {speakNotice}
        </p>
      ) : null}

      {voiceCostNotice}

      {paintConfirmation}

      <div
        ref={streamViewportRef}
        className="chat__stream scroll-area"
        onScroll={handleTimelineScroll}
      >
        {timelineHasMore || timelineLoading || timelineError ? (
          <div className="chat__history-loader" aria-live="polite">
            <button
              type="button"
              className="chat__history-loader-button"
              disabled={timelineLoading}
              onClick={() => void loadEarlierMessages()}
            >
              {timelineLoading
                ? "正在找今天更早的消息…"
                : timelineError
                  ? "刚才没加载出来，点这里再试"
                  : "继续上拉，看看今天更早的消息"}
            </button>
          </div>
        ) : null}

        {chat.state.phase === "loading" && !topupThankPending ? (
          /*
           * 这一段的等待比它看起来长得多。
           *
           * `chat/enter` 里包着一次**真实的模型调用**（开场白 / 续场），超时被
           * 专门抬到 45 秒，实测 deep 档 4–8 秒、赶上重试 20 秒以上。这里曾经是
           * 一颗 6px 的呼吸点加一行 12px 小字塞在药丸里——一个几乎看不见的东西
           * 撑一次可能长达十几秒的等待，用户只会以为卡住了。
           */
          <div className="chat__connecting" role="status">
            <div className="chat__connecting-sky" aria-hidden>
              <span className="chat__connecting-bf chat__connecting-bf--1">
                <Butterfly className="butterfly--night" />
              </span>
              <span className="chat__connecting-bf chat__connecting-bf--2">
                <Butterfly className="butterfly--night" />
              </span>
              <span className="chat__connecting-bf chat__connecting-bf--3">
                <Butterfly className="butterfly--night" />
              </span>
            </div>
            <p className="chat__connecting-text">…正在连上灵境…</p>
          </div>
        ) : null}

        {chat.state.phase === "failed" ? (
          <MiaoNotice
            scene="conversation"
            error={chat.state.error}
            onRetry={reloadChat}
            expected={[
              "当前会话的历史消息，按 sequence 排序",
              "她这次该不该主动开口，以及那句开场白",
              "服务端下发的输入长度上限与当前形态",
              "剩余灵力与本轮消耗",
            ]}
          />
        ) : null}

        {displayedTimeline.map((item) =>
          item.kind === "channel_encounter"
            ? renderEncounter(item)
            : renderPersistedMessage(item.message),
        )}

        {/*
          乐观条目：还没落库，服务端认不出这个 id，所以只给复制。
          朗读、评价、删除全都要一个真实的 messageId——MessageBubble 内部
          按 `delivery` 自己会挡住它们，这里不重复判断。
        */}
        {localEntries.map((entry) => {
          const messageAppearance = appearanceForForm(
            meta,
            messageFormKey(entry.message, currentForm),
            appearance,
          );
          return (
            <MessageBubble
              key={entry.message.id}
              entry={entry}
              showAiGeneratedMark={aiDisclosureEnabled}
              speakerName={messageAppearance.name}
              avatarUrl={messageAppearance.avatarUrl}
              onRetry={retry}
              onNameClick={() => setFormSheetOpen(true)}
              open={openActionsId === entry.message.id}
              onToggleOpen={() =>
                setOpenActionsId((id) =>
                  id === entry.message.id ? null : entry.message.id,
                )
              }
              onCopy={copyText}
              imagePreviewUrls={imagePreviewUrls}
              onImagePreviewConsumed={consumeImagePreview}
            />
          );
        })}

        {/*
          正在逐字长出来的那条。放在乐观条目之后：用户刚说的那句在上、
          她的回答在下，与落库之后的顺序一致，提交那一刻不会跳位。

          不给它任何操作条——消息还没落库，复制之外的每一个动作都需要
          一个服务端认得的 messageId。
        */}
        {streamEntry ? (
          <MessageBubble
            key={streamEntry.message.id}
            entry={streamEntry}
            showAiGeneratedMark={aiDisclosureEnabled}
            speakerName={currentAppearance.name}
            avatarUrl={currentAppearance.avatarUrl}
            streaming={streaming}
            onNameClick={() => setFormSheetOpen(true)}
          />
        ) : null}

        {awaitingFirstToken ? (
          <TypingIndicator
            speakerName={currentAppearance.name}
            avatarUrl={currentAppearance.avatarUrl}
            activityText={toolActivityText}
          />
        ) : null}

      </div>

      {/*
        灵力见底的公告。放在输入区上方而不是弹窗里：这是个持续状态，
        弹一次就消失的话，用户过一会儿又会疑惑她怎么变了。
      */}
      {downgraded ? (
        <p className="chat__notice chat__notice--form" role="status">
          灵力用完了，她现在是笨笨缈缈。
        </p>
      ) : null}

      {/*
        称呼框停在输入区的位置上，**顶掉输入框**。
        与舞台模式同一条规矩：她刚问完「我该怎么称呼你呀」，此刻该回答的是这个，
        旁边再摆一个能聊别的的输入框，等于给了两条岔路，
        而走另一条的人这辈子都不会再被问一次。跳过之后输入框立刻回来。
      */}
      <div ref={dockRef} className="chat__dock">
        {namePrompt ?? (canSend ? composer : null)}
      </div>

      {formSheet}
    </section>
  );
}
